import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import AIInsightsCard from "@/components/AIInsightsCard";
import RoleDashboardWidgets from "@/components/RoleDashboardWidgets";
import GroupLeadDashboard from "@/components/GroupLeadDashboard";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  UsersFour, UserCircle, Handshake, Trophy, Target, TrendUp, CalendarBlank, Fire,
} from "@phosphor-icons/react";

const CHART = { width: "100%", height: 260 };
const TICK = { fontSize: 11, fill: "#475569" };
const RADIUS = [4, 4, 0, 0];

const Stat = ({ label, value, icon: Icon, tone = "sky", testid, hint }) => (
  <div className="stat-card" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-3xl font-semibold mt-2 text-slate-900">{value}</div>
        {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
      </div>
      <div className={`w-10 h-10 rounded-md grid place-items-center bg-${tone}-50 text-${tone}-600`}>
        <Icon size={20} />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [personal, setPersonal] = useState(null);
  const [team, setTeam] = useState(null);
  const [company, setCompany] = useState(null);
  const [kpis, setKpis] = useState(null);

  useEffect(() => {
    api.get("/dashboard/personal").then((r) => setPersonal(r.data)).catch(() => {});
    api.get("/dashboard/kpis-plus").then((r) => setKpis(r.data)).catch(() => {});
    if (user?.role !== "executive" && user?.role !== "sde" && user?.role !== "sdl") {
      api.get("/dashboard/team").then((r) => setTeam(r.data)).catch(() => {});
      api.get("/dashboard/company").then((r) => setCompany(r.data)).catch(() => {});
    }
  }, [user?.role]);

  const p = personal || { today: {}, week: {}, month: {} };
  const k = kpis || {};

  // Phase 10 — Group Leads see the operational dashboard exclusively.
  if (user?.role === "sdl") {
    return (
      <div className="space-y-8" data-testid="dashboard-page">
        <GroupLeadDashboard />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Phase 6C — role-based dashboard widgets (from /api/dashboards/*) */}
      <RoleDashboardWidgets />

      <section>
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h2 className="text-2xl font-semibold">Hi {user?.name?.split(" ")[0]},</h2>
            <p className="text-sm text-slate-500">Here's your snapshot for today.</p>
          </div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Target achievement · <span className="text-sky-700 font-semibold">{p.achievement_pct || 0}%</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat testid="stat-today-total" label="Applications Today" value={p.today?.total ?? 0} icon={Target} tone="sky" />
          <Stat testid="stat-today-cp" label="Career Portal" value={p.today?.career_portal ?? 0} icon={TrendUp} tone="emerald" />
          <Stat testid="stat-today-ea" label="Easy Apply" value={p.today?.easy_apply ?? 0} icon={TrendUp} tone="amber" />
          <Stat testid="stat-today-cold" label="Cold Emails" value={p.today?.cold_emails ?? 0} icon={Handshake} tone="violet" />
          <Stat testid="stat-week" label="This Week" value={p.week?.total ?? 0} icon={TrendUp} tone="sky" />
          <Stat testid="stat-month" label="This Month" value={p.month?.total ?? 0} icon={TrendUp} tone="emerald" />
          <Stat testid="stat-pending" label="My Candidates" value={p.pending_candidates ?? 0} icon={UserCircle} tone="amber" />
          <Stat testid="stat-daily-target" label="Daily Target" value={p.daily_target ?? 0} icon={Target} tone="slate" />
        </div>
      </section>

      {/* Phase 3 Expanded KPIs */}
      <section data-testid="kpis-plus-section">
        <h3 className="text-lg font-semibold mb-3">Candidate operations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat testid="kpi-active" label="Active" value={k.active_candidates ?? 0} icon={UserCircle} tone="emerald" />
          <Stat testid="kpi-hold" label="On Hold" value={k.hold_candidates ?? 0} icon={UserCircle} tone="amber" />
          <Stat testid="kpi-completed" label="Completed" value={k.completed_candidates ?? 0} icon={Trophy} tone="sky" />
          <Stat testid="kpi-apps-today" label="Applications Today" value={k.applications_today ?? 0} icon={Fire} tone="rose" />
          <Stat testid="kpi-placements" label="Placements · Last 30d" value={k.placements_last_30d ?? 0} icon={Trophy} tone="violet" />
        </div>

        {/* Upcoming lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
          <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="upcoming-deadlines">
            <h4 className="font-semibold mb-2 flex items-center gap-2"><CalendarBlank size={16} /> Deadlines · next 7 days</h4>
            {(k.upcoming_deadlines || []).length === 0 && <div className="text-sm text-slate-400 py-4 text-center">Nothing due soon 🎉</div>}
            <ul className="divide-y divide-slate-100">
              {(k.upcoming_deadlines || []).map((d) => (
                <li key={d.id} className="py-2 flex items-center justify-between text-sm">
                  <Link to={`/candidates/${d.id}`} className="text-sky-700 hover:underline">{d.full_name}</Link>
                  <span className="text-xs text-red-600 font-medium">{d.deadline_date}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="upcoming-evals">
            <h4 className="font-semibold mb-2 flex items-center gap-2"><CalendarBlank size={16} /> Evaluations · next 7 days</h4>
            {(k.upcoming_evaluations || []).length === 0 && <div className="text-sm text-slate-400 py-4 text-center">No sessions scheduled.</div>}
            <ul className="divide-y divide-slate-100">
              {(k.upcoming_evaluations || []).map((e) => (
                <li key={e.id} className="py-2 flex items-center justify-between text-sm">
                  <Link to={`/candidates/${e.candidate_id}`} className="text-sky-700 hover:underline">{e.candidate_id.slice(0, 8)}…</Link>
                  <span className="text-xs text-slate-500">{e.evaluation_date} · {e.evaluation_time}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {team && (
        <section>
          <h3 className="text-lg font-semibold mb-3">Team overview</h3>
          <div className="mb-4"><AIInsightsCard /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Employees" value={team.total_employees} icon={UsersFour} />
            <Stat label="Working today" value={team.working_today} icon={UsersFour} tone="emerald" />
            <Stat label="Active candidates" value={team.total_candidates} icon={UserCircle} tone="amber" />
            <Stat label="Offers total" value={team.offers} icon={Trophy} tone="violet" />
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5 mt-6">
            <div className="flex items-baseline justify-between mb-4">
              <h4 className="font-semibold">Leaderboard (This month)</h4>
              <span className="text-xs text-slate-500">Applications</span>
            </div>
            <div style={CHART}>
              <ResponsiveContainer>
                <BarChart data={team.leaderboard}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={TICK} />
                  <YAxis tick={TICK} />
                  <Tooltip />
                  <Bar dataKey="applications" fill="#0284C7" radius={RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      )}

      {company && (
        <section>
          <h3 className="text-lg font-semibold mb-3">Company KPIs</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Stat label="Interview rate" value={`${company.interview_rate}%`} icon={TrendUp} />
            <Stat label="Offer rate" value={`${company.offer_rate}%`} icon={Trophy} tone="emerald" />
            <Stat label="Success rate" value={`${company.success_rate}%`} icon={Target} tone="amber" />
            <Stat label="Career Portal %" value={`${company.career_portal_pct}%`} icon={TrendUp} />
            <Stat label="Easy Apply %" value={`${company.easy_apply_pct}%`} icon={TrendUp} tone="emerald" />
            <Stat label="YTD Applications" value={company.year?.total ?? 0} icon={TrendUp} tone="violet" />
          </div>
        </section>
      )}
    </div>
  );
}
